import { Heading } from '@tiptap/extension-heading';
import { mergeAttributes } from '@tiptap/core';

/**
 * Clean markdown formatting from text
 */
function cleanMarkdown(text: string): string {
  return text
    // Remove bold (**text** or __text__)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    // Remove italic (*text* or _text_)
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    // Remove inline code (`text`)
    .replace(/`(.+?)`/g, '$1')
    // Remove strikethrough (~~text~~)
    .replace(/~~(.+?)~~/g, '$1')
    // Remove links [text](url)
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    // Remove images ![alt](url)
    .replace(/!\[.*?\]\(.+?\)/g, '')
    .trim();
}

/**
 * Generate a slug from text for use as an anchor ID
 */
function generateSlug(text: string): string {
  const cleanedText = cleanMarkdown(text);

  return cleanedText
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Extract text content from a TipTap node
 */
function getTextContent(node: any): string {
  let text = '';

  if (node.text) {
    text += node.text;
  }

  if (node.content) {
    node.content.forEach((child: any) => {
      text += getTextContent(child);
    });
  }

  return text;
}

/**
 * Custom Heading extension that automatically adds anchor IDs
 * based on the heading text content
 */
export const HeadingWithAnchors = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      id: {
        default: null,
        parseHTML: element => element.getAttribute('id'),
        renderHTML: attributes => {
          if (!attributes.id) {
            return {};
          }
          return {
            id: attributes.id,
          };
        },
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const hasLevel = this.options.levels.includes(node.attrs.level);
    const level = hasLevel ? node.attrs.level : this.options.levels[0];

    // Generate ID from heading text content
    const textContent = getTextContent(node);
    const id = textContent ? generateSlug(textContent) : null;

    // Add the ID to HTML attributes if it exists
    const attrs = id ? { ...HTMLAttributes, id } : HTMLAttributes;

    return [
      `h${level}`,
      mergeAttributes(this.options.HTMLAttributes, attrs),
      0,
    ];
  },
});
